import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import ProductSwiper from "@components/swiper/ProductSwiper";
import { useTheme } from "@emotion/react";
import ColorBall from "@components/item/ColorBall";
import { useNavigate, useParams } from "react-router-dom";
import useUserInfoStore from "@store/useUserInfoStore";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { Flex } from "antd";
import BasicButton from "@components/button/BasicButton";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useApiRequest } from "@utils/ApiRequest";
import { formatDate } from "@utils/functions";

type Props = {
  productInfo: any;
};

const Stocked = (props: Props) => {
  const theme = useTheme();
  const [selectedColor, setSelectedColor] = useState(null);
  const { productInfo } = props;
  const [deleteModal, setDeleteModal] = useState(false);
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { deleteProductStock } = useApiRequest();
  const { id, status } = useParams();

  const selectedColorItem = productInfo?.color_list?.find(
    (item: any) => item.color_id === selectedColor,
  );

  const isUserProduct = userInfo?.id === productInfo?.seller_id;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSelectedColor(productInfo?.color_list?.[0]?.color_id);
  }, [productInfo]);

  const handleMenuClick = () => {
    setOpenDrawer(true);
  };

  const { mutate: deleteProductMutate } = useMutation({
    mutationFn: () => deleteProductStock(Number(productInfo.product_id)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getProductInventorySimple"],
      });
      navigate("/capture-list", {
        replace: true,
      });
    },
  });

  return (
    <Container>
      <EditButtonContainer>
        {isUserProduct && (
          <>
            <EditMenuButton>
              <StyledImage
                onClick={handleMenuClick}
                src={theme.image.dot_icon.default}
              />
            </EditMenuButton>
          </>
        )}
        <ProductSwiper imageList={productInfo?.product_image_list} />
      </EditButtonContainer>

      <ProductInfoWrapper>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: "2rem" }}>
          <Data>{formatDate(productInfo?.updated_at)}</Data>
          <Data>재고등록</Data>
        </Flex>

        <CategoryBox>
          <span>
            {productInfo?.category_ko
              ? productInfo?.category_ko
              : productInfo?.category_en}
          </span>
        </CategoryBox>

        <BrandWithTitle>
          <div className="brand_name">
            <span className="brand">
              {productInfo?.brand_name_ko
                ? productInfo?.brand_name_ko
                : productInfo?.brand_name_en}
            </span>
          </div>

          <span className="title ellipsis-1">
            {productInfo?.product_name_ko
              ? productInfo?.product_name_ko
              : productInfo?.product_name_en}
          </span>
        </BrandWithTitle>

        <ConditionBox>
          <span className="label">{"컬러"}</span>
          <div className="color_box">
            {productInfo?.color_list?.map((item: any) => (
              <ColorBall
                color={item.hex_code}
                colorName={item.name}
                key={item.color_id}
                onClick={() => setSelectedColor(item.color_id)}
                active={item.color_id === selectedColor}
              />
            ))}
          </div>
        </ConditionBox>

        {selectedColorItem && (
          <ConditionBox>
            <span className="label">사이즈(수량)</span>

            <div className="color_box">
              {selectedColorItem?.size_list?.map((size: any) => (
                <SizeItem
                  className="size"
                  key={size.id}
                  disabled={size.amount === 0}>
                  {size.name} ({size.amount})
                </SizeItem>
              ))}
            </div>
          </ConditionBox>
        )}
      </ProductInfoWrapper>

      <ButtonWrapper>
        <BasicButton
          title="샤카라카에 상품 등록"
          width="100%"
          onClick={() =>
            navigate(`/register/${id}/${status}`, {
              state: {
                origin: "stocked",
                returnTo: `/product-detail/${id}/${status}`,
              },
            })
          }
        />
      </ButtonWrapper>

      <BasicDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        wrapperPadding="0"
        padding="0">
        <ItemBox>
          <Item
            onClick={() =>
              navigate(
                `/stock-edit/${productInfo.product_id}/${status}`,
              )
            }>
            {"수정하기"}
          </Item>

          <Item onClick={() => setDeleteModal(true)}>{"삭제하기"}</Item>
        </ItemBox>
      </BasicDrawer>

      <BasicModal
        open={deleteModal}
        setOpen={setDeleteModal}
        warning
        activeEvent={() => {
          deleteProductMutate(productInfo?.product_id);
          setDeleteModal(false);
        }}
        activeName="삭제"
        closeName="취소"
        padding="6rem 2rem 2rem 2rem">
        <ModalContent mainMessage="이 상품을 삭제하시겠습니까?" />
      </BasicModal>
    </Container>
  );
};

export default Stocked;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 5.6rem)",
});

const ProductInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2rem",
  flex: 1,
}));

const Data = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
}));

const CategoryBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.color.gray,
}));

const BrandWithTitle = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "1rem",
  paddingBottom: "1rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  ".brand_name": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.8rem",
  },

  ".brand": {
    fontSize: "2rem",
    fontWeight: theme.fontWeight.semiBold,
    lineHeight: "2rem",
  },
}));

const ConditionBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: theme.color.gray,

  ".label": {
    minWidth: "8rem",
  },

  ".color_box": {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
}));

const SizeItem = styled.span<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: "0.7rem",
  color: disabled ? theme.color.light_gray : theme.color.black,
  minHeight: "3rem",
}));

const EditButtonContainer = styled.div(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const EditMenuButton = styled.div(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  width: "100%",
  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",
  height: "50px",
}));

const StyledImage = styled.img(({ theme }) => ({
  display: "flex",
  objectFit: "cover",
  position: "absolute",
  right: "1.6rem",
  top: "1rem",
  cursor: "pointer",
}));

const ItemBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

const Item = styled.button(({ theme }) => ({
  width: "100%",
  minHeight: "5.6rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.color.border_gray}`,

  "&:first-child": {
    border: "none",
  },

  "&:last-child": {
    color: theme.color.orange,
    justifyContent: "center",
  },
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  padding: "1rem",
  width: "100%",
}));
